import {cloneDeep} from 'lodash';
import {UpdatePatientInformationRequestDTO} from '../../dtos/patient';
import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {environment} from 'src/environments/environment';
import {IUser} from '../auth';
import {IPatient} from './patient.interface';
import {IPatientsIndicators} from './patients-indicators.interface';
import {IServicePatient} from './service-patient.interface';
import {IIndicatorsPatientConsultation} from '../measurement';
import {firstValueFrom, Observable} from 'rxjs';
import {ServicePatientStatusEnum} from './service-patient-status.enum';
import {ClinicActions, ClinicState, IClinic} from '../clinic';
import {IDeletePatientInformation} from '../../interfaces/patient';
import {IAllergy} from '../record-nutritional-history';

export const INDICATORS_PATIENT_CONSULTATION: IIndicatorsPatientConsultation = {
  measurements: {
    high: null,
    weight: null,
    imc: null
  },
  servicePatient: {
    measurementList: [],
    clinicObjectives: [],
    status: ServicePatientStatusEnum.ACTIVE,
    patientObjective: '',
    lastAppointmentDate: new Date(),
    patientGoal: '',
  }
};

export interface IRPatient {
  patients: IPatient[];
  count: number;
  assistants: IPatient[];
  selectedId: string;
  patientsIndicators: IPatientsIndicators;
  patientSelectedInformation?: IIndicatorsPatientConsultation;
  patientCreated?: IPatient;
  hasCompletedOnboarding?: {expertId: string | null};
  deletePatientInformation?: IDeletePatientInformation;
  allergicIngredients: string[];
  dislikesIngredients: string[];
}

export namespace PatientAction {
  export class GetById {
    static readonly type = '[PATIENTS] Get Patient By Id Action';
    constructor(public id: string, public clinicId: string) { }
  }

  export class HasCompletedOnboarding {
    static readonly type = '[PATIENTS] Get Patient Has Completed Onboarding Action';
    constructor(public id: string) { }
  }

  export class GetByAppointment {
    static readonly type = '[PATIENTS] Get Patients By Appointment Action';
    constructor(public id: string) { }
  }


  export class SetSelectedById {
    static readonly type = '[PATIENTS] Set Selected Patient Id Action';
    constructor(public id: string) { }
  }

  export class SetPatients {
    static readonly type = '[PATIENTS] Set Patients Action';
    constructor(public patients: IPatient[]) { }
  }

  export class SetAssistants {
    static readonly type = '[PATIENTS] Set Assistants Action';
    constructor(public assistants: IPatient[]) { }
  }
  export class CountByClinicId {   // borrar
    static readonly type = '[PATIENTS] Get Patients Count By ClinicId Action';
    constructor(public clinicId: string, public search: string = '', public status: string) { }
  }

  export class Patch {
    static readonly type = '[PATIENTS] Patch Patient Action';
    constructor(public clinicId: string, public patientId: string, public patient: UpdatePatientInformationRequestDTO) { }
  }

  export class Post {
    static readonly type = '[PATIENTS] Register Patient Action';
    constructor(public clinicId: string, public user: IUser) { }
  }

  export class GetIndicators {
    static readonly type = '[PATIENTS] Get Patients Indicators Action';
    constructor(public clinicId: string, public timeZoneOverride: string) { }
  }

  export class SetPatient {
    static readonly type = '[PATIENTS] Add Patient Action';
    constructor(public patient: IPatient) { }
  }

  export class GetInformation {
    static readonly type = '[PATIENTS] Get Patient Information Indicators Action';
    constructor(public clinicId: string, public patientId: string, public serviceId: string) { }
  }

  export class ResetInformation {
    static readonly type = '[PATIENTS] Reset Patient Information Indicators Action';
    constructor() { }
  }

  export class Delete {
    static readonly type = '[PATIENTS] Delete Action';
    constructor(public patientId: string) { }
  }

  export class DeletePatientInformation {
    static readonly type = '[PATIENTS] Delete Patient Information Action';
    constructor(public patientId: string) { }
  }

  export class DeleteDemoPatientFromList {
    static readonly type = '[PATIENTS] Delete Demo Patient From Patients Action';
    constructor() { }
  }

  export class ResetState {
    static readonly type = '[PATIENTS] Reset Patient State Action';
    constructor() { }
  }

  export class PatchInformation {
    static readonly type = '[PATIENTS] Patch Patient Information Action';
    constructor(public patientId: string, public serviceId: string, public data: Partial<IServicePatient>) { }
  }

  export class AddAssistantToList {
    static readonly type = '[PATIENTS] Add Assistant To List Action';
    constructor(public assistant: any) { }
  }

  export class GetAllergicIngredients {
    static readonly type: string = '[PATIENTS] Get Allergic Ingredients';
    constructor(public patientId: string, public serviceId: string) { }
  }

  export class GetDislikesIngredients {
    static readonly type: string = '[PATIENTS] Get Dislikes Ingredients';
    constructor(public patientId: string, public serviceId: string) { }
  }
}

const _DEFAULT_PATIENT_STATE: IRPatient = {
  patients: [],
  count: 0,
  assistants: [],
  selectedId: '',
  patientsIndicators: null,
  allergicIngredients: [],
  dislikesIngredients: []
};

@State<IRPatient>({
  name: 'PatientState',
  defaults: cloneDeep(_DEFAULT_PATIENT_STATE),
})

@Injectable()
export class PatientState {
  constructor(
    private baseService: BaseService<any>,
    private store: Store
  ) { }

  url = environment.SERVER_URL;

  @Selector()
  static getSelectedPatient(state: IRPatient): IPatient {
    return state.patients.find((patient: IPatient) => patient._id === state.selectedId);
  }

  @Selector()
  static getDeletePatientInformation({deletePatientInformation}: IRPatient): IDeletePatientInformation | undefined {
    return deletePatientInformation;
  }

  @Selector()
  static getPatients(state: IRPatient): any[] {
    return state.patients;
  }

  @Selector()
  public static getPatientByUserId(state: IRPatient): (id: string) => IPatient {
    return (id: string) => {
      return state.patients.find((item) => item.userId === id);
    };
  }

  @Selector()
  static getAssistants(state: IRPatient): IPatient[] {
    return state.assistants;
  }

  @Selector()
  static getAssistantsCount(state: IRPatient): number {
    return state.count;
  }

  @Selector()
  static getPatientsIndicators(state: IRPatient): IPatientsIndicators {
    return state.patientsIndicators;
  }

  @Selector()
  static getPatientSelectedInformation(state: IRPatient): IIndicatorsPatientConsultation {
    return state.patientSelectedInformation;
  }

  @Selector()
  static getPatientCreated(state: IRPatient): IPatient {
    return state.patientCreated;
  }

  @Selector()
  static getPatientHasCompletedOnboarding(state: IRPatient): any {
    return state.hasCompletedOnboarding;
  }

  @Selector()
  static getPatientAllergicIngredients(state: IRPatient): string[] {
    return state.allergicIngredients;
  }

  @Selector()
  static getPatientDislikesIngredients(state: IRPatient): string[] {
    return state.dislikesIngredients;
  }

  @Action(PatientAction.GetById)
  async getPatientByIdAction(ctx: StateContext<IRPatient>, action: PatientAction.GetById): Promise<void> {
    const response: DataBaseServiceResponse<IPatient>  = await firstValueFrom(this.baseService.get(`${this.url}/patients/${action.id}`));

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      selectedId: action.id,
      patients: [response.entity]
    });
  }

  @Action(PatientAction.HasCompletedOnboarding)
  async getPatientHasCompletedOnboardingAction(ctx: StateContext<IRPatient>, action: PatientAction.HasCompletedOnboarding): Promise<void> {
    const response: DataBaseServiceResponse<{expertId: string | null}> = await firstValueFrom(
      this.baseService.get(`${this.url}/patients/${action.id}/has-completed-onboarding`)
    );

    if (response.success && response.entity || response.message === 'Error: (PA003) Patient has not been invited to a clinic.') {
      ctx.patchState({
        hasCompletedOnboarding: response.entity
      });
    } else {
      throw new Error(response.message);
    }
  }

  @Action(PatientAction.GetByAppointment)
  async getPatientsByAppointmentAction(ctx: StateContext<IRPatient>, action: PatientAction.GetByAppointment): Promise<void> {
    const response: DataBaseServiceResponse<IPatient[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/appointments/${action.id}/patients`, void 0)
    );

    if (response.error) throw new Error(response.message);
    ctx.patchState({
      patients: response.entity
    });
  }

  @Action(PatientAction.SetPatients)
  async setPatientsAction(ctx: StateContext<IRPatient>, action: PatientAction.SetPatients): Promise<void> {
    ctx.patchState({
      patients: action.patients
    });
  }

  @Action(PatientAction.SetAssistants)
  async setAssistantsAction(ctx: StateContext<IRPatient>, action: PatientAction.SetAssistants): Promise<void> {
    ctx.patchState({
      assistants: action.assistants
    });
  }

  @Action(PatientAction.Patch)
  async patchPatientAction(ctx: StateContext<IRPatient>, action: PatientAction.Patch): Promise<void> {
    const response = await firstValueFrom(
      this.baseService.patch(`${this.url}/patients/${action.patientId}`, action.patient)
    );

    if (response.error) throw new Error(response.message);


    const patients = cloneDeep(ctx.getState().patients || []);
    const index = patients.findIndex(patient => patient._id === action.patientId);
    if (index !== -1) {
      if (action.patient.civilState) patients[index].civilState = action.patient.civilState;
      patients[index].user = {...patients[index].user, ...action.patient};

      ctx.patchState({
        patients
      });
    }
  }

  @Action(PatientAction.SetPatient)
  async addPatientAction(ctx: StateContext<IRPatient>, action: PatientAction.SetPatient): Promise<void> {
    const patients = cloneDeep(ctx.getState().patients);
    if (action.patient) {
      patients.unshift(action.patient);
      ctx.patchState({
        patients
      });
    }
  }

  @Action(PatientAction.Post)
  async registerPatientAction(ctx: StateContext<IRPatient>, action: PatientAction.Post): Promise<void> {
    const response: DataBaseServiceResponse<IPatient> = await firstValueFrom(
      this.baseService.post(`${this.url}/auth/register/${action.clinicId}/patient`, action.user)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({ patientCreated: response.entity });
  }

  @Action(PatientAction.SetSelectedById)
  async setSelectedPatientId(ctx: StateContext<IRPatient>, action: PatientAction.SetSelectedById): Promise<void> {
    ctx.patchState({
      selectedId: action.id
    });
  }

  @Action(PatientAction.CountByClinicId)
  async getPatientsCountByClinicIdAction(ctx: StateContext<IRPatient>, action: PatientAction.CountByClinicId): Promise<void> {
    const response: DataBaseServiceResponse<{count: number}> = await firstValueFrom(
      this.baseService.get(`${this.url}/clinics/${action.clinicId}/patients/count`, void 0, void 0, {
        params: {
          search: action.search,
          status: action.status
        }
      })
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      count: response.entity && response.entity.count ? response.entity.count : 0
    });
  }

  @Action(PatientAction.GetIndicators)
  async getPatientsIndicatorsAction(ctx: StateContext<IRPatient>, action: PatientAction.GetIndicators): Promise<void> {
    const response: DataBaseServiceResponse<IPatientsIndicators> = await firstValueFrom(
      this.baseService.get(`${this.url}/statistics/${action.clinicId}/patients`, void 0, void 0, {
        params: {
          timeZone: action.timeZoneOverride,
        }
      })
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      patientsIndicators: response.entity
    });
  }

  @Action(PatientAction.GetInformation)
  async getPatientInformationAction(ctx: StateContext<IRPatient>, action: PatientAction.GetInformation): Promise<void> {
    const response: DataBaseServiceResponse<IIndicatorsPatientConsultation> = await firstValueFrom(
      this.baseService.get(`${this.url}/patients/${action.clinicId}/${action.patientId}/${action.serviceId}/information`)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      patientSelectedInformation: response.entity
    });
  }

  @Action(PatientAction.PatchInformation)
  async patchPatientInformationAction(ctx: StateContext<IRPatient>, action: PatientAction.PatchInformation): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(`${this.url}/services/${action.serviceId}/patients/${action.patientId}/service-patients/patient-goal`, action.data)
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(PatientAction.ResetInformation)
  async resetPatientInformationAction(ctx: StateContext<IRPatient>, _: PatientAction.ResetInformation): Promise<void> {
    ctx.patchState({
      patientSelectedInformation: INDICATORS_PATIENT_CONSULTATION
    });
  }

  @Action(PatientAction.ResetState)
  async resetPatientStateAction(ctx: StateContext<IRPatient>, _: PatientAction.ResetState): Promise<void> {
    ctx.patchState(cloneDeep(_DEFAULT_PATIENT_STATE));
  }

  @Action(PatientAction.DeleteDemoPatientFromList)
  async deleteDemoPatientFromPatientsAction(ctx: StateContext<IRPatient>, _: PatientAction.DeleteDemoPatientFromList): Promise<void> {
    const patients: IPatient[] = ctx.getState().patients;
    if (!patients) return;

    const clinic: IClinic = await firstValueFrom(this.store.selectOnce(ClinicState.getClinicSelected));
    if (!clinic) return;

    await firstValueFrom(
      this.store.dispatch(new ClinicActions.GetPatients(clinic._id, '', void 0, void 0))
    );
  }

  @Action(PatientAction.AddAssistantToList)
  async addAssistantToListAction(ctx: StateContext<IRPatient>, action: PatientAction.AddAssistantToList): Promise<void> {
    if (!action.assistant) {
      return;
    }

    const assistants: IPatient[] = cloneDeep(ctx.getState().assistants);
    assistants.unshift(action.assistant);

    ctx.patchState({assistants});
  }

  @Action(PatientAction.Delete)
  async deletePatient(ctx: StateContext<IRPatient>, action: PatientAction.Delete): Promise<void> {
    const request: Observable<DataBaseServiceResponse<void>> = this.baseService.delete(`${this.url}/patients/${action.patientId}`);
    await firstValueFrom(request);
  }

  @Action(PatientAction.GetAllergicIngredients)
  async getAllergicIngredients(ctx: StateContext<IRPatient>, action: PatientAction.GetAllergicIngredients): Promise<void> {
    const request: DataBaseServiceResponse<IAllergy[]> = await firstValueFrom(this.baseService.get(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/allergies`));
    if (request.error) {
      throw new Error(request.message);
    }
    ctx.patchState({
      allergicIngredients: [...request.entity.map((ingredient: IAllergy) => ingredient.ingredientId)]
    });
  }

  @Action(PatientAction.GetDislikesIngredients)
  async getDislikesIngredients(ctx: StateContext<IRPatient>, action: PatientAction.GetDislikesIngredients): Promise<void> {
    const request: DataBaseServiceResponse<IAllergy[]> = await firstValueFrom(this.baseService.get(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/food-dislikes`));
    if (request.error) {
      throw new Error(request.message);
    }
    if (!request.entity) {return};
    ctx.patchState({
      dislikesIngredients: [...request.entity.map((ingredient: IAllergy) => ingredient.ingredientId)]
    });
  }

  @Action(PatientAction.DeletePatientInformation)
  async deletePatientInformation({patchState}: StateContext<IRPatient>, {patientId}: PatientAction.DeletePatientInformation): Promise<void> {
    const response: DataBaseServiceResponse<IDeletePatientInformation> = await firstValueFrom(
      this.baseService.get(`${this.url}/patients/${patientId}/delete-information`)
    );
    if (response.error) throw new Error(response.message);

    patchState({deletePatientInformation: response.entity});
  }
}
